/**
 * @author: lv.chen
 * @date: 2025/8/7
 * @desc: ReportsContentManagementCenter组件，报告内容管理中心
 */
import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { history, useDispatch, useIntl } from 'umi';
import { useDebounceFn } from 'ahooks';
import { Modal, Justify, Button, SearchBox, Table, notification } from 'tea-component';
import ReportUploader from '@home/components/ReportUploader';
import EllipsisHover from '@home/components/EllipsisHover';
import CardSlotV2 from '@intelligence/components/CardSlotV2';
import useTableAutotipAddon from '@home/components/TeaComponents/Table/_hooks/useTableAutotipAddon';
import usePageLog, { reportButtonLog, reportSearchLog } from '@user-center/_hooks/usePageLog';
import useReportAdminMode from './_hooks/useReportAdminMode';
import { deleteReports } from '@user-center/services/report';
import { fetchReports } from './services';
import { generateSortKey } from '@intelligence/v3/utils/common';
import { getOptionText } from '@intelligence/utils/topchartsUtils';
import { getLocationText, getSegmentText } from './utils';
import { getMarketReportLocationText, getMarketReportSegmentText } from './MarketReports/utils';
import { SortType } from '@home/enums/common';
import { ReportState } from '@user-center/enums/reports';
import { PAGE_SIZES } from '@gp/constants/const';
import { recognizeReportService } from '@home/services/report';
import { REPORT_DATA_MODEL_NAMESPACE } from '@home/models/report';
import { MARKET_REPORT_CATEGORY_VALUE } from './MarketReports/constants';
import { appendSearchParams } from '@home/utils/urlConfig';

import type { PagingQuery, TableColumn } from 'tea-component';
import type { SortBy } from 'tea-component/lib/table/addons';

import './index.scss';

const { sortable, pageable } = Table.addons;

const iconList: any[] = [];

const recordKey = (cvm: IReport) => cvm?.resource_id || '';

interface IReportsContentManagementCenterProps extends IBaseProps {}

const ReportsContentManagementCenter: React.FC<IReportsContentManagementCenterProps> = (props) => {
  const { style, className } = props;

  const dispatch = useDispatch();

  const { locale, formatMessage } = useIntl();

  const [loading, setLoading] = useState(true);

  const [keyword, setKeyword] = useState('');

  const [pagination, setPagination] = useState<PagingQuery>({ pageIndex: 1, pageSize: 20 });

  const [total, setTotal] = useState(0);

  const [sorts, setSorts] = useState<SortBy<IReport>[]>([{ by: 'update_time', order: SortType.Desc }]);

  const [reports, setReports] = useState<IReport[]>([]);

  const { loading: adminModeLoading, adminMode } = useReportAdminMode();

  const sortAddon = sortable({
    columns: ['update_time'],
    value: sorts,
    onChange: (value: SortBy<IReport>[]) => {
      const paginationNew = { ...pagination, pageIndex: 1 };
      updateReports(adminMode, keyword, value, paginationNew);
      setSorts(value);
      setPagination(paginationNew);
    },
  });

  const pageableAddon = pageable({
    pageSizeOptions: PAGE_SIZES,
    recordCount: total,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    onPagingChange: (query: PagingQuery) => {
      updateReports(adminMode, keyword, sorts, query);
      setPagination(query);
    },
  });

  const autotipAddon = useTableAutotipAddon({
    isLoading: loading,
    loadingText: formatMessage({ id: 'db_loading' }),
    emptyText: formatMessage({ id: 'dbi_search_empty' }),
  });

  const addons = [sortAddon, pageableAddon, autotipAddon];

  const columns: TableColumn<IReport>[] = [
    {
      key: 'title',
      header: formatMessage({ id: 'dbu_report_title' }),
      align: 'left',
      width: 170,
      render: (cvm) => {
        const titleCN = cvm?.title;
        const titleEN = cvm?.title_en;
        const reportTitle = (
          <>
            {ReportState.UnPublished === cvm?.state && (
              <span className="dbu-report-title__dangerous-text">[{formatMessage({ id: 'dbu_common_draft' })}]</span>
            )}
            <span>{getOptionText(locale, { value: '', text: titleEN, textEN: titleEN, textCN: titleCN })}</span>
          </>
        );

        return <EllipsisHover className="dbu-report-title" text={reportTitle} />;
      },
    },
    {
      key: 'cover',
      header: formatMessage({ id: 'dbu_common_cover' }),
      align: 'left',
      width: 140,
      render: (cvm) => {
        return <img className="dbu-content-management-center-reports-cover" src={cvm?.cover} alt="" />;
      },
    },
    {
      key: 'location',
      header: formatMessage({ id: 'dbu_report_location' }),
      align: 'left',
      width: 170,
      render: (cvm) => {
        if (cvm?.system === MARKET_REPORT_CATEGORY_VALUE) {
          return getMarketReportLocationText(locale, cvm.system, cvm?.source);
        }
        return getLocationText(locale, cvm?.system, cvm?.source, cvm?.sub_source);
      },
    },
    {
      key: 'segment',
      header: formatMessage({ id: 'dbi_filter_segment' }),
      align: 'left',
      width: 170,
      render: (cvm) => {
        if (cvm?.system === MARKET_REPORT_CATEGORY_VALUE) {
          return getMarketReportSegmentText(locale, cvm?.source);
        }
        return getSegmentText(locale, cvm?.system, cvm?.source, cvm?.sub_source);
      },
    },
    {
      key: 'update_time',
      header: formatMessage({ id: 'dbu_common_update_time' }),
      align: 'left',
      width: 190,
      render: (cvm) => {
        return cvm?.update_time;
      },
    },
    {
      key: 'actions',
      header: formatMessage({ id: 'dbi_actions' }),
      align: 'left',
      width: 140,
      render: (cvm) => {
        return (
          <div>
            <Button
              type="link"
              onClick={(e) => {
                e?.stopPropagation();
                const resourceId = cvm?.resource_id;
                if (!!resourceId) {
                  reportButtonLog('contentManagementCenterReportsEditSubmit', { extInfo: resourceId });
                  let editPath = '/user-center/content-management-center/reports/edit';
                  if (cvm?.system === MARKET_REPORT_CATEGORY_VALUE) {
                    editPath = '/user-center/content-management-center/reports/market-reports/edit';
                  }
                  history.push(appendSearchParams(editPath, { resourceId }));
                }
              }}
            >
              {formatMessage({ id: 'Edit' })}
            </Button>
            <Button
              type="link"
              onClick={(e) => {
                e?.stopPropagation();
                Modal.confirm({
                  message: formatMessage({ id: 'dbu_report_delete_confirm_title' }),
                  description: formatMessage({ id: 'dbu_report_delete_confirm_desc' }),
                  okText: formatMessage({ id: 'dbh_confirm' }),
                  cancelText: formatMessage({ id: 'dbh_cancel' }),
                  onOk: async () => {
                    try {
                      const resourceId = cvm?.resource_id || '';
                      const resourceIds = !resourceId ? [] : [resourceId];
                      await deleteReports({ admin_mode: adminMode, resource_ids: resourceIds });
                      updateReports(adminMode);
                      reportButtonLog('contentManagementCenterReportsDeleteSubmit', { extInfo: resourceId });
                      notification.success({
                        unique: true,
                        description: formatMessage({ id: 'dbu_common_delete_success_tips' }),
                      });
                    } catch (error) {
                      console.error(error);
                      notification.error({
                        unique: true,
                        description: formatMessage({ id: 'dbu_common_delete_failure_tips' }),
                      });
                    }
                  },
                });
              }}
            >
              {formatMessage({ id: 'Delete' })}
            </Button>
          </div>
        );
      },
    },
  ];

  const updateReports = async (
    adminModePayload = adminMode,
    keywordPayload = keyword,
    sortsPayload = sorts,
    paginationPayload = pagination,
  ) => {
    try {
      setLoading(true);
      const orderBys = sortsPayload?.map((sort) => {
        return generateSortKey(sort?.by, sort?.order as SortType);
      });
      const res = await fetchReports({
        admin_mode: adminModePayload,
        keyword: keywordPayload,
        order_by: orderBys || [],
        page: paginationPayload.pageIndex || 1,
        page_size: paginationPayload.pageSize || 20,
      });
      const resData = res?.data;
      const total = resData?.total || 0;
      const reports = resData?.list || [];

      setTotal(total);
      setReports(reports);
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const { run: updateReportsDebounce } = useDebounceFn(updateReports, { wait: 600 });

  useEffect(() => {
    if (adminModeLoading) {
      return;
    }
    updateReports(adminMode);
  }, [adminModeLoading, adminMode]);

  usePageLog('contentManagementCenterReports');

  return (
    <div style={style} className={classnames('dbu-content-management-center-reports-page', className)}>
      <Justify
        className="dbu-content-management-center-reports-justify"
        left={
          <div className="dbu-content-management-center-reports-actions">
            <Button
              type="primary"
              onClick={(e) => {
                e?.stopPropagation();
                reportButtonLog('contentManagementCenterReportsAddSubmit');
                history.push('/user-center/content-management-center/reports/add');
              }}
            >
              {formatMessage({ id: 'dbu_report_share_new_report' })}
            </Button>
            {adminMode && (
              <ReportUploader
                cancelable
                recognize={recognizeReportService}
                onRecognized={(file, result) => {
                  dispatch({
                    type: `${REPORT_DATA_MODEL_NAMESPACE}/updateReportRecognizeResult`,
                    payload: { file, result },
                  });
                  history.push('/user-center/content-management-center/reports/market-reports/add');
                }}
                button={<Button>{formatMessage({ id: 'dbu_report_share_market_report' })}</Button>}
              />
            )}
          </div>
        }
        right={
          <SearchBox
            className="dbu-content-management-center-reports-search-box"
            placeholder={formatMessage({ id: 'dbu_report_search_placeholder' })}
            value={keyword}
            onChange={(value) => {
              const paginationNew = { ...pagination, pageIndex: 1 };
              updateReportsDebounce(adminMode, value, sorts, paginationNew);
              setKeyword(value);
              setPagination(paginationNew);
              reportSearchLog('contentManagementCenterReportsSearch', { query: value });
            }}
          />
        }
      />
      <CardSlotV2 iconList={iconList}>
        <Table
          className="dbu-content-management-center-reports-table"
          recordKey={recordKey}
          addons={addons}
          columns={columns}
          records={loading ? [] : reports}
        />
      </CardSlotV2>
    </div>
  );
};

export default ReportsContentManagementCenter;
